import React, { useState, useEffect } from 'react';
import { Content, Page } from '@backstage/core-components';
import { Button, Typography, makeStyles } from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import SentimentDissatisfiedIcon from '@material-ui/icons/SentimentDissatisfied';
import HomeIcon from '@material-ui/icons/Home';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

const useStyles = makeStyles(theme => ({
  '@global': {
  '[class*="BackstagePage-root"]': {
    display: 'block !important',
    height: 'auto !important',
    gridTemplateRows: 'unset !important',
    gridTemplateAreas: 'unset !important',
    gridTemplateColumns: 'unset !important',
},
},

  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '85vh',
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  errorCode: {
    fontSize: '10rem',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: '$pulse 2s infinite',
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
      transform: 'scale(1)',
    },
    '50%': {
      opacity: 0.8,
      transform: 'scale(1.05)',
    },
    '100%': {
      opacity: 1,
      transform: 'scale(1)',
    },
  },
  sadFace: {
    fontSize: '5rem',
    color: theme.palette.primary.main,
    animation: '$bounce 2s infinite',
    marginBottom: theme.spacing(2),
  },
  '@keyframes bounce': {
    '0%, 100%': {
      transform: 'translateY(0)',
    },
    '50%': {
      transform: 'translateY(-20px)',
    },
  },
  errorMessage: {
    fontSize: '2rem',
    fontWeight: 500,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
  },
  errorDescription: {
    fontSize: '1.2rem',
    maxWidth: '600px',
    marginBottom: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginBottom: theme.spacing(4),
  },
  button: {
    padding: theme.spacing(1, 3),
    borderRadius: '50px',
    fontWeight: 'bold',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
    },
  },
  homeButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  backButton: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  helpButton: {
    borderColor: theme.palette.grey[400],
    color: theme.palette.text.secondary,
  },
  linksContainer: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    maxWidth: '800px',
  },
  linksTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  linksList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: theme.spacing(2),
    textAlign: 'left',
  },
  link: {
    color: theme.palette.primary.main,
    display: 'block',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    textDecoration: 'none',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      textDecoration: 'none',
    },
  },
  dotGrid: {
    position: 'absolute',
    zIndex: -1,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: 'radial-gradient(circle, #ccc 1px, transparent 1px)',
    backgroundSize: '30px 30px',
    opacity: 0.2,
  },
}));

export const CustomNotFoundPage = () => {
  const classes = useStyles();
  const [randomTip, setRandomTip] = useState('');

  // URL for detecting what the user might have been looking for
  const currentPath = window.location.pathname;
  
  const helpfulTips = [
    'Check for typos in the URL',
    'Use the navigation menu to find what you need',
    'Try using the search feature',
    'Check if you have permission to access this page',
    'The page might have been moved or renamed',
  ];


  useEffect(() => {
    // Set a random tip when the component mounts
    const tip = helpfulTips[Math.floor(Math.random() * helpfulTips.length)];
    setRandomTip(tip);
  }, []);

  const goBack = () => {
    window.history.back();
  };

  return (
    <Page themeId="home">
      <Content>
        <div className={classes.dotGrid} />
        <div className={classes.root}>
          <SentimentDissatisfiedIcon className={classes.sadFace} />
          <Typography variant="h1" className={classes.errorCode}>
            404
          </Typography>
          <Typography variant="h2" className={classes.errorMessage}>
            Page Not Found
          </Typography>
          <Typography variant="body1" className={classes.errorDescription}>
            The page you're looking for doesn't exist or has been moved.
            <br />
            <strong>Path:</strong> {currentPath}
            <br />
            <strong>Tip:</strong> {randomTip}
          </Typography>
          
          <div className={classes.buttonContainer}>
            <Button 
              variant="contained" 
              className={`${classes.button} ${classes.homeButton}`}
              component={RouterLink}
              to={window.location.pathname.startsWith('/sts') ? '/sts/infrastructure-stats' : '/marketplace/home'}
              startIcon={<HomeIcon />}
            >
              {window.location.pathname.startsWith('/sts') ? 'STS' : 'Marketplace'}
            </Button>
            <Button 
              variant="contained" 
              className={`${classes.button} ${classes.backButton}`}
              onClick={goBack}
              startIcon={<ArrowBackIcon />}
            >
              Go Back
            </Button>
          </div>
          
        </div>
      </Content>
    </Page>
  );
};